import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { OWNER_PARAM, outreachEmailPath } from '@/lib/outreach-email';
import { offerDraft } from '@/lib/outreach-email-draft';
import { useChat } from '@ai-sdk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import {
  ArrowLeftIcon,
  BrainIcon,
  CheckIcon,
  HistoryIcon,
  Loader2Icon,
  PaperclipIcon,
  SparklesIcon,
  PencilIcon,
  SquarePenIcon,
  StarIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  XIcon,
} from 'lucide-react';
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import { Message, MessageContent, MessageResponse } from '@/components/ai-elements/message';
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  usePromptInputAttachments,
} from '@/components/ai-elements/prompt-input';
import { ConfirmDialog, EmptyState, ErrorNotice } from '@bcecloud/portal-ui';
import { isAwaitingReply } from './awaiting-reply';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { authorizationHeader, getJson, sendJson } from '@/lib/api';
import { ATTACHMENT_ACCEPT } from '@/lib/events';
import { cn } from '@/lib/utils';
import { assistantMarkdownComponents, DraftEmailCard } from '@/components/assistant/rich';
import { MemoryView } from '@/components/assistant/MemoryView';
import { changedProposalIds, notifyProposalChanged } from '@/proposals/lib/live';
import {
  clearSavedMessages,
  getCurrentChatId,
  getSavedMessages,
  saveMessages,
  setCurrentChatId,
} from '@/components/assistant/assistant-store';

export interface AssistantPanelProps {
  readonly onClose: () => void;
}

/** What the server may be told about the current screen. */
interface ScreenContext {
  readonly screen:
    | 'events'
    | 'event'
    | 'shipments'
    | 'recaps'
    | 'programs'
    | 'program'
    | 'people'
    | 'organizations'
    | 'proposals'
    | 'proposal'
    | 'client';
  readonly eventId?: string;
  /** The Repeat clients client open on screen (their page or their email page). */
  readonly accountId?: string;
  /** The proposal open in the editor, so "this proposal" resolves without an id. */
  readonly proposalId?: string;
}

/**
 * Maps the current route to the screen context sent with each message. The
 * server does the actual data fetching — the panel only says where you are.
 *
 * @param pathname - The current location pathname.
 * @returns The screen context, or null on unrecognized routes.
 */
function contextFromPath(pathname: string): ScreenContext | null {
  const client = /^\/repeat-clients\/(?:clients|email)\/(001[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?)(?:\/|$)/.exec(pathname);
  if (client?.[1] !== undefined) return { screen: 'client', accountId: client[1] };
  const event = /^\/events\/([a-f0-9]{24})/.exec(pathname);
  if (event?.[1] !== undefined) return { screen: 'event', eventId: event[1] };
  const proposal = /^\/proposals\/([a-f0-9]{24})/.exec(pathname);
  if (proposal?.[1] !== undefined) return { screen: 'proposal', proposalId: proposal[1] };
  if (pathname.startsWith('/proposals')) return { screen: 'proposals' };
  if (pathname.startsWith('/events')) return { screen: 'events' };
  if (pathname.startsWith('/shipments')) return { screen: 'shipments' };
  if (pathname.startsWith('/recaps')) return { screen: 'recaps' };
  if (/^\/programs\/[a-f0-9]{24}/.test(pathname)) return { screen: 'program' };
  if (pathname.startsWith('/programs')) return { screen: 'programs' };
  if (pathname.startsWith('/people')) return { screen: 'people' };
  if (pathname.startsWith('/organizations')) return { screen: 'organizations' };
  return null;
}

/** The browser-run tool that puts the assistant's draft into a client's email page. */
const DRAFT_CLIENT_EMAIL_TOOL = 'draftClientEmail';
const draftInputSchema = z.object({
  accountId: z.string().regex(/^001[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/),
  contactId: z.string().regex(/^003[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/),
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(10_000),
  templateId: z.string().nullable().catch(null),
});

/** Friendly labels for tool activity chips. */
const TOOL_LABELS: Readonly<Record<string, string>> = {
  findEvents: 'Searching events',
  findPrograms: 'Searching programs',
  accountHistory: 'Pulling client history',
  searchRecaps: 'Searching recaps',
  shipmentSweep: 'Checking shipments',
  searchNotes: 'Searching notes',
  eventPreflight: 'Running pre-flight',
  eventAttachments: 'Finding attachments',
  findFacilitators: 'Finding facilitators',
  facilitatorStats: 'Crunching facilitator history',
  topFacilitators: 'Building the leaderboard',
  recentEvents: 'Finding recent events',
  navigate: 'Opening',
  memoryList: 'Checking memory',
  memoryRead: 'Recalling',
  memoryWrite: 'Updating memory',
  memorySearch: 'Searching memory',
  memoryDelete: 'Forgetting',
  emailRecipientSearch: 'Finding recipient',
  emailPreview: 'Drafting email',
  findProposals: 'Searching proposals',
  findProposalOpportunities: 'Finding opportunities',
  readProposal: 'Reading the proposal',
  proposalPricingPreview: 'Pricing it out',
  readClientEmails: 'Reading their emails',
  draftClientEmail: 'Putting the draft in the email page',
};

/**
 * App paths the assistant may navigate to. Anything else is refused — the
 * model can suggest, but only allowlisted in-app destinations ever load.
 */
const SAFE_NAV_PATHS: readonly RegExp[] = [
  /^\/(events|programs|shipments|recaps|people|organizations|proposals|resources)(\/[a-f0-9]{24})?(\?tab=[a-z-]+)?$/i,
  // The AI Assistant pages and their detail views.
  /^\/assistant\/(skills|memory|chats|schedules)$/,
  /^\/assistant\/skills\/(user|global)\/[a-z0-9][a-z0-9-]*$/,
  /^\/assistant\/(memory|chats|schedules)\/[a-f0-9]{24}$/,
];

/** Whether the assistant may load this destination. */
function isSafeNavPath(to: string): boolean {
  return SAFE_NAV_PATHS.some((pattern) => pattern.test(to));
}

/** A message part, loosely typed around the tool-invocation shape. */
export interface ToolPart {
  readonly type: string;
  readonly state?: string;
  readonly toolName?: string;
  readonly output?: unknown;
}

/** How many results a tool returned, when its output makes that inferable. */
function resultCount(output: unknown): number | null {
  if (typeof output !== 'object' || output === null) return null;
  const o = output as Record<string, unknown>;
  for (const key of ['count', 'total']) {
    if (typeof o[key] === 'number') return o[key] as number;
  }
  for (const key of ['events', 'programs', 'shipments', 'notes', 'recaps', 'facilitators', 'leaders', 'checks', 'proposals', 'opportunities', 'resources', 'topics']) {
    if (Array.isArray(o[key])) return (o[key] as unknown[]).length;
  }
  return null;
}

/**
 * One tool invocation as a status chip — "Searching events… → 3" — so a
 * multi-step answer shows its work instead of looking stalled.
 *
 * @param props - The tool message part.
 * @returns The chip.
 */
export function ToolChip({ part }: { readonly part: ToolPart }): React.ReactElement {
  const name = part.toolName ?? part.type.replace(/^tool-/, '');
  const label = TOOL_LABELS[name] ?? `Running ${name}`;
  const done = part.state === 'output-available';
  const failed = part.state === 'output-error';
  const count = done ? resultCount(part.output) : null;

  return (
    <span className="my-1 inline-flex items-center gap-1.5 rounded-full border border-admin-line bg-admin-bg px-2.5 py-1 text-admin-micro font-medium text-admin-muted">
      {failed ? (
        <XIcon aria-hidden className="size-3 text-red-500" />
      ) : done ? (
        <CheckIcon aria-hidden className="size-3 text-emerald-600" />
      ) : (
        <Loader2Icon aria-hidden className="size-3 animate-spin text-brand" />
      )}
      {label}
      {failed ? ' — failed' : done && count !== null ? ` → ${count}` : done ? '' : '…'}
    </span>
  );
}

/**
 * Shown while the assistant works with nothing else on screen — after send,
 * and between a finished tool and the next words — so a slow answer never
 * looks stalled.
 *
 * @returns The indicator row.
 */
function ThinkingIndicator(): React.ReactElement {
  return (
    <div role="status" className="flex items-center gap-2 text-portal-small text-admin-muted">
      <Loader2Icon aria-hidden className="size-4 motion-safe:animate-spin text-brand" />
      Thinking…
    </div>
  );
}

/** The concatenated plain text of a message's text parts. */
function messageText(message: UIMessage): string {
  return message.parts
    .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
    .map((part) => part.text)
    .join('\n');
}

/**
 * Serialises a conversation for storage: text parts as-is, tool parts slimmed
 * to their name and done-state (inputs/outputs are bulky and rehydrate as a
 * plain ✓ chip), everything else dropped.
 *
 * @param messages - The live conversation.
 * @returns Storage-ready messages.
 */
function serializeChat(messages: UIMessage[]): unknown[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    // Keep dropped-file refs so a reopened chat still shows its attachments.
    ...(attachmentsOf(message).length > 0 ? { metadata: { attachments: attachmentsOf(message) } } : {}),
    parts: message.parts
      .filter((part) => part.type === 'text' || part.type.startsWith('tool-') || part.type === 'dynamic-tool')
      .map((part) =>
        part.type === 'text'
          ? part
          : {
              type: part.type,
              state: 'output-available',
              ...((part as ToolPart).toolName ? { toolName: (part as ToolPart).toolName } : {}),
            },
      ),
  }));
}

/** A file dropped into the chat, referenced by metadata only — never inlined into the model. */
interface ChatAttachmentRef {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly size: number;
}

/** Uploads one composer file to the assistant attachment store; returns its ref, or null on failure. */
async function uploadChatFile(
  file: { url?: string; filename?: string; mediaType?: string },
  auth: Record<string, string>,
  chatId: string | null,
): Promise<ChatAttachmentRef | null> {
  if (!file.url) return null;
  try {
    const blob = await (await fetch(file.url)).blob();
    const form = new FormData();
    form.append('file', new File([blob], file.filename ?? 'file', { type: file.mediaType ?? blob.type }));
    if (chatId) form.append('chatId', chatId);
    const res = await fetch('/api/v3/assistant/attachments', {
      method: 'POST',
      headers: auth,
      body: form,
    });
    if (!res.ok) return null;
    const json = (await res.json().catch(() => null)) as { data?: Partial<ChatAttachmentRef> } | null;
    const d = json?.data;
    return d && typeof d.id === 'string'
      ? { id: d.id, name: String(d.name ?? file.filename ?? 'file'), type: String(d.type ?? ''), size: Number(d.size ?? 0) }
      : null;
  } catch {
    return null;
  }
}

/** The dropped-file refs a message carries (in its metadata) for display and persistence. */
function attachmentsOf(message: UIMessage): ChatAttachmentRef[] {
  const meta = (message as { metadata?: unknown }).metadata;
  const list = meta && typeof meta === 'object' ? (meta as { attachments?: unknown }).attachments : undefined;
  return Array.isArray(list) ? (list as ChatAttachmentRef[]) : [];
}

/** Pending file chips and a paperclip to add more, shown inside the composer. */
function ComposerAttachments(): React.ReactElement | null {
  const { files, remove, openFileDialog } = usePromptInputAttachments();
  if (files.length === 0) {
    return (
      <button
        type="button"
        onClick={openFileDialog}
        title="Attach a file"
        aria-label="Attach a file"
        className="absolute bottom-2 left-2 z-10 grid size-7 place-items-center rounded-portal-button text-portal-muted hover:bg-portal-bg hover:text-portal-ink"
      >
        <PaperclipIcon className="size-4" />
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1 pb-1.5">
      <button
        type="button"
        onClick={openFileDialog}
        title="Attach a file"
        aria-label="Attach a file"
        className="grid size-7 place-items-center rounded-portal-button text-portal-muted hover:bg-portal-bg hover:text-portal-ink"
      >
        <PaperclipIcon className="size-4" />
      </button>
      {files.map((f) => (
        <span
          key={f.id}
          className="inline-flex max-w-[180px] items-center gap-1 rounded-portal-pill border border-portal-line bg-portal-bg px-2 py-0.5 text-portal-micro text-portal-ink"
        >
          <span className="truncate">{f.filename ?? 'file'}</span>
          <button type="button" onClick={() => remove(f.id)} aria-label={`Remove ${f.filename ?? 'file'}`} className="text-portal-muted hover:text-portal-ink">
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

/** A sent message's file chips (openable), rendered from its metadata. */
function SentAttachments({ files }: { files: readonly ChatAttachmentRef[] }): React.ReactElement | null {
  if (files.length === 0) return null;
  return (
    <span className="mb-1 flex flex-wrap gap-1.5">
      {files.map((f) => (
        <a
          key={f.id}
          href={`/api/v3/attachments/${f.id}/view`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-[200px] items-center gap-1 rounded-portal-pill border border-portal-line bg-white/70 px-2 py-0.5 text-portal-micro font-medium text-brand no-underline hover:underline"
        >
          <PaperclipIcon className="size-3 shrink-0" />
          <span className="truncate">{f.name}</span>
        </a>
      ))}
    </span>
  );
}

/** A chat's title: its first user message, trimmed to fit. */
function titleFrom(messages: UIMessage[]): string {
  const first = messages.find((message) => message.role === 'user');
  const text = first ? messageText(first).replace(/\s+/g, ' ').trim() : '';
  return text.length > 0 ? text.slice(0, 80) : 'New chat';
}

/** A row in the chat-history list. */
interface ChatListItem {
  readonly id: string;
  readonly title: string;
  readonly pinned: boolean;
  readonly updatedAt: string | null;
}

/**
 * 👍/👎 under an assistant answer. One vote per answer; stored server-side so
 * the team can see where the assistant helps and where it fails.
 *
 * @param props - The answer text, the question that produced it, the screen.
 * @returns The feedback row.
 */
function FeedbackRow({
  text,
  question,
  screen,
}: {
  readonly text: string;
  readonly question: string | undefined;
  readonly screen: string | undefined;
}): React.ReactElement {
  const [sent, setSent] = useState<'up' | 'down' | null>(null);

  function vote(rating: 'up' | 'down'): void {
    if (sent !== null) return;
    setSent(rating);
    // Fire-and-forget by design: feedback must never interrupt the chat.
    void sendJson('post', '/assistant/feedback', {
      rating,
      message: text.slice(0, 8000),
      ...(question ? { question: question.slice(0, 2000) } : {}),
      ...(screen ? { screen } : {}),
    }).catch(() => undefined);
  }

  const buttonClass = (active: boolean): string =>
    cn(
      'grid size-6 cursor-pointer place-items-center rounded-md',
      active ? 'bg-brand/10 text-brand' : 'text-portal-muted/60 hover:bg-admin-subtle hover:text-portal-ink',
      sent !== null && !active && 'opacity-40',
    );

  return (
    <span className="mt-1 flex items-center gap-1">
      <button
        type="button"
        aria-label="Good answer"
        aria-pressed={sent === 'up'}
        onClick={() => vote('up')}
        className={buttonClass(sent === 'up')}
      >
        <ThumbsUpIcon aria-hidden className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label="Bad answer"
        aria-pressed={sent === 'down'}
        onClick={() => vote('down')}
        className={buttonClass(sent === 'down')}
      >
        <ThumbsDownIcon aria-hidden className="size-3.5" />
      </button>
      {sent !== null ? (
        <span className="text-admin-micro text-admin-muted">Thanks — recorded.</span>
      ) : null}
    </span>
  );
}

/** A small icon button with a fast tooltip. */
function HeaderButton({
  label,
  onClick,
  children,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className="grid size-8 cursor-pointer place-items-center rounded-portal-button text-portal-muted hover:bg-portal-bg hover:text-portal-ink"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The staff assistant: a chat column docked to the right of the content.
 *
 * Conversations auto-save to the caller's personal, server-side chat history
 * (each user sees only their own); the History view lists them with pin-to-top
 * favorites and delete. The open chat also survives closing the panel via a
 * module store.
 *
 * @param props - Close handler for the panel chrome.
 * @returns The assistant panel.
 */
export function AssistantPanel({ onClose }: AssistantPanelProps): React.ReactElement {
  const location = useLocation();
  // Read through a ref at request time: the transport stays stable (so the
  // conversation survives navigation) while each message still carries the
  // screen the person is on *now*.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // The event this CONVERSATION last talked about — not the current screen.
  // When the person navigates to a different event mid-conversation, the next
  // message flags the switch so the server can tell the model that earlier
  // turns belong to the other event. Getting confused across events is worse
  // than any single wrong answer.
  const conversationEventIdRef = useRef<string | null>(null);

  // Files dropped with the current message, uploaded before send. Read by the
  // transport body for the turn's request, then cleared — so the model is told
  // about them (metadata only) without the bytes ever entering the prompt.
  const pendingAttachmentsRef = useRef<ChatAttachmentRef[]>([]);
  // The dropped-file refs shown on each SENT user message, keyed by message id.
  // Kept in our own state rather than trusting the AI SDK to persist message
  // metadata, so the chip reliably shows after send.
  const [sentAttachments, setSentAttachments] = useState<Record<string, ChatAttachmentRef[]>>({});

  const [view, setView] = useState<'chat' | 'history' | 'memory'>('chat');
  const [deleting, setDeleting] = useState<ChatListItem | null>(null);
  const queryClient = useQueryClient();

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: '/api/v3/assistant/chat',
        // authorizationHeader, not a staff bearer: inside Salesforce the
        // panel's credential is the proposal editor's Editor token.
        headers: () => authorizationHeader(),
        body: () => {
          const context = contextFromPath(pathRef.current);
          // Flag a mid-conversation event switch, then move the anchor. The
          // anchor survives non-event screens on purpose: events A → shipments
          // → event B is still a switch the model must hear about.
          const previous = conversationEventIdRef.current;
          const switched =
            context?.eventId !== undefined && previous !== null && previous !== context.eventId;
          if (context?.eventId !== undefined) conversationEventIdRef.current = context.eventId;
          // The saved chat id rides along as memory-write provenance.
          const chatId = getCurrentChatId();
          const attachments = pendingAttachmentsRef.current;
          return {
            ...(context === null
              ? {}
              : {
                  context: {
                    ...context,
                    ...(switched && previous !== null ? { previousEventId: previous } : {}),
                  },
                }),
            ...(chatId === null ? {} : { chatId }),
            ...(attachments.length > 0 ? { attachments } : {}),
          };
        },
      }),
    [],
  );

  const navigate = useNavigate();

  const { messages, sendMessage, setMessages, addToolResult, status, stop, error } = useChat({
    transport,
    messages: getSavedMessages(),
    // The navigate tool has no server execute — the browser is its runtime.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onToolCall: ({ toolCall }) => {
      if (toolCall.toolName === DRAFT_CLIENT_EMAIL_TOOL) {
        const parsed = draftInputSchema.safeParse(toolCall.input);
        if (parsed.success) {
          offerDraft(parsed.data);
          // A manager working another rep's client keeps that rep on the email page.
          const owner = new URLSearchParams(location.search).get(OWNER_PARAM) ?? undefined;
          navigate(outreachEmailPath(parsed.data.accountId, owner));
        }
        void addToolResult({
          tool: DRAFT_CLIENT_EMAIL_TOOL,
          toolCallId: toolCall.toolCallId,
          output: parsed.success ? { placed: true, page: 'email page' } : { error: 'The draft was missing the client, contact, subject or body.' },
        });
        return;
      }
      if (toolCall.toolName !== 'navigate') return;
      const to = String((toolCall.input as { to?: unknown } | undefined)?.to ?? '');
      const allowed = isSafeNavPath(to);
      if (allowed) navigate(to);
      void addToolResult({
        tool: 'navigate',
        toolCallId: toolCall.toolCallId,
        output: allowed ? { navigated: true, to } : { error: 'That destination is not allowed.' },
      });
    },
    // When the assistant edits a proposal, tell the open editor to refresh.
    onFinish: ({ message }) => {
      for (const id of changedProposalIds((message as { parts?: unknown[] }).parts)) notifyProposalChanged(id);
    },
  });

  // Keep the module store current so reopening the panel restores the chat.
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  // Auto-save each completed exchange into the personal history. Saves only
  // when the stream settles, and only when something changed.
  const lastSavedRef = useRef('');
  useEffect(() => {
    if (status !== 'ready' || messages.length === 0) return;
    const serialized = serializeChat(messages);
    const fingerprint = JSON.stringify(serialized);
    if (fingerprint === lastSavedRef.current) return;
    lastSavedRef.current = fingerprint;
    void (async () => {
      try {
        const chatId = getCurrentChatId();
        if (chatId === null) {
          const created = await sendJson<ChatListItem>('post', '/assistant/chats', {
            title: titleFrom(messages),
            messages: serialized,
          });
          setCurrentChatId(created.id);
        } else {
          await sendJson('put', `/assistant/chats/${chatId}`, { messages: serialized });
        }
        await queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      } catch {
        // History is a convenience; a failed save must never break the chat.
      }
    })();
  }, [status, messages, queryClient]);

  const chats = useQuery({
    queryKey: ['assistant-chats'],
    queryFn: ({ signal }) => getJson<ChatListItem[]>('/assistant/chats', undefined, signal),
    enabled: view === 'history',
  });

  const togglePinChat = useMutation({
    mutationFn: (chat: ChatListItem) =>
      sendJson('put', `/assistant/chats/${chat.id}`, { pinned: !chat.pinned }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assistant-chats'] }),
  });

  const renameChat = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      sendJson('put', `/assistant/chats/${id}`, { title }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assistant-chats'] }),
  });

  const deleteChat = useMutation({
    mutationFn: (id: string) => sendJson('delete', `/assistant/chats/${id}`),
    onSuccess: async (_data, id) => {
      if (getCurrentChatId() === id) {
        setCurrentChatId(null);
      }
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
    },
  });

  /** Opens a saved chat in the conversation view. */
  async function openChat(id: string): Promise<void> {
    try {
      const chat = await getJson<ChatListItem & { messages: UIMessage[] }>(`/assistant/chats/${id}`);
      setMessages(chat.messages);
      setCurrentChatId(id);
      // A restored conversation's event anchor is unknown — reset rather than
      // guess; the next event screen visited re-anchors it.
      conversationEventIdRef.current = null;
      lastSavedRef.current = JSON.stringify(serializeChat(chat.messages));
      setView('chat');
    } catch {
      // Leave the list open; the row simply doesn't respond.
    }
  }

  /** Starts a fresh conversation (the previous one is already auto-saved). */
  function newChat(): void {
    clearSavedMessages();
    setMessages([]);
    setCurrentChatId(null);
    conversationEventIdRef.current = null;
    lastSavedRef.current = '';
    setView('chat');
  }

  // Resizable width, persisted per browser. Dragging the left edge resizes.
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = Number(window.localStorage.getItem('bce.assistant.width'));
      return Number.isFinite(stored) && stored >= 320 ? Math.min(stored, 720) : 400;
    } catch {
      return 400;
    }
  });
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  function onResizeStart(event: React.PointerEvent<HTMLDivElement>): void {
    resizeRef.current = { startX: event.clientX, startWidth: width };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function onResizeMove(event: React.PointerEvent<HTMLDivElement>): void {
    const state = resizeRef.current;
    if (state === null) return;
    const next = Math.min(720, Math.max(320, state.startWidth + (state.startX - event.clientX)));
    setWidth(next);
  }
  function onResizeEnd(): void {
    if (resizeRef.current === null) return;
    resizeRef.current = null;
    try {
      window.localStorage.setItem('bce.assistant.width', String(width));
    } catch {
      // Width just won't persist.
    }
  }

  const currentContext = contextFromPath(location.pathname);
  const subtitle =
    view === 'history'
      ? 'Your chats — only you can see these'
      : view === 'memory'
        ? 'What it remembers — yours to edit or forget'
        : currentContext?.screen === 'event'
          ? 'Sees this event and its notes'
          : 'Knows which screen you are on';

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={300}>
      <aside
        aria-label="BCE assistant"
        // The CSS var carries the resizable width so the phone override below
        // can win with a plain class — an inline `width` would beat it.
        style={{ '--assistant-width': `${width}px` } as React.CSSProperties}
        // Phone: a fixed overlay filling the DYNAMIC viewport (dvh — 100vh
        // hides the composer behind Safari's chrome), full width, above the
        // shell. ≥720px: the docked, resizable column beside the content.
        className="fixed inset-0 z-40 flex h-dvh w-full flex-col border-portal-line bg-white min-[720px]:sticky min-[720px]:inset-auto min-[720px]:top-topbar min-[720px]:z-auto min-[720px]:h-[calc(100vh-var(--spacing-topbar))] min-[720px]:w-(--assistant-width) min-[720px]:shrink-0 min-[720px]:border-l"
      >
        {/* Drag the left edge to resize; double-click resets. Pointless on a
            phone, where the panel is always full-width. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize assistant"
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onDoubleClick={() => setWidth(400)}
          className="absolute inset-y-0 -left-1 z-10 hidden w-2 cursor-col-resize hover:bg-brand/20 active:bg-brand/30 min-[720px]:block"
        />
        <header className="flex shrink-0 items-center justify-between border-b border-portal-line py-2 pr-2 pl-4">
          <span className="min-w-0">
            <span className="flex items-center gap-2 text-portal-small font-bold">
              <SparklesIcon aria-hidden="true" className="size-4 text-brand" />
              BCE assistant
            </span>
            <span className="block truncate text-portal-micro text-portal-muted">{subtitle}</span>
          </span>
          <span className="flex shrink-0 items-center">
            {view !== 'chat' ? (
              <HeaderButton label="Back to chat" onClick={() => setView('chat')}>
                <ArrowLeftIcon aria-hidden className="size-4" />
              </HeaderButton>
            ) : (
              <>
                <HeaderButton label="New chat" onClick={newChat}>
                  <SquarePenIcon aria-hidden className="size-4" />
                </HeaderButton>
                <HeaderButton label="Chat history" onClick={() => setView('history')}>
                  <HistoryIcon aria-hidden className="size-4" />
                </HeaderButton>
                <HeaderButton label="Memory" onClick={() => setView('memory')}>
                  <BrainIcon aria-hidden className="size-4" />
                </HeaderButton>
              </>
            )}
            <HeaderButton label="Close assistant" onClick={onClose}>
              <XIcon aria-hidden="true" className="size-4" />
            </HeaderButton>
          </span>
        </header>

        {view === 'memory' ? (
          <MemoryView />
        ) : view === 'history' ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {chats.isPending ? (
              <p className="p-4 text-portal-small text-portal-muted">Loading chats…</p>
            ) : chats.isError ? (
              <ErrorNotice inline title="History unavailable" message="Could not load your chats." />
            ) : chats.data.length === 0 ? (
              <EmptyState
                icon={<HistoryIcon aria-hidden className="size-6" />}
                title="No chats yet"
                message="Conversations save here automatically. Pin the ones you come back to."
              />
            ) : (
              <div className="space-y-2">
                {chats.data.map((chat) => (
                  <ChatCard
                    key={chat.id}
                    chat={chat}
                    onOpen={() => void openChat(chat.id)}
                    onTogglePin={() => togglePinChat.mutate(chat)}
                    onRename={(title) => renameChat.mutate({ id: chat.id, title })}
                    onDelete={() => setDeleting(chat)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <Conversation className="min-h-0 flex-1">
            <ConversationContent className="gap-5 px-4 py-5">
              {messages.length === 0 ? (
                <ConversationEmptyState
                  icon={<SparklesIcon className="size-5" />}
                  title="Ask about your events"
                  description="Search events, check shipments, run a pre-flight, find a facilitator — or draft a note."
                />
              ) : (
                messages.map((message, index) => {
                  const question =
                    message.role === 'assistant'
                      ? messages
                          .slice(0, index)
                          .reverse()
                          .find((m) => m.role === 'user')
                      : undefined;
                  return (
                    <Message from={message.role} key={message.id}>
                      <MessageContent>
                        {message.role === 'user' ? (
                          <SentAttachments files={sentAttachments[message.id] ?? attachmentsOf(message)} />
                        ) : null}
                        {message.parts.map((part, partIndex) => {
                          if (part.type === 'text') {
                            return message.role === 'assistant' ? (
                              <MessageResponse
                                key={`${message.id}-${partIndex}`}
                                components={assistantMarkdownComponents}
                              >
                                {part.text}
                              </MessageResponse>
                            ) : (
                              <span key={`${message.id}-${partIndex}`} className="whitespace-pre-wrap">
                                {part.text}
                              </span>
                            );
                          }
                          if (part.type.startsWith('tool-') || part.type === 'dynamic-tool') {
                            // A successful email draft ALWAYS renders its
                            // approval card, straight from the tool result —
                            // never dependent on the model formatting a link.
                            const draftId =
                              part.type === 'tool-emailPreview' &&
                              (part as ToolPart).state === 'output-available'
                                ? ((part as ToolPart).output as { draftId?: string } | undefined)
                                    ?.draftId
                                : undefined;
                            return (
                              <span key={`${message.id}-${partIndex}`} className="block">
                                <ToolChip part={part as ToolPart} />
                                {draftId ? <DraftEmailCard draftId={draftId} /> : null}
                              </span>
                            );
                          }
                          return null;
                        })}
                        {message.role === 'assistant' &&
                        messageText(message).length > 0 &&
                        (status !== 'streaming' || index !== messages.length - 1) ? (
                          <FeedbackRow
                            text={messageText(message)}
                            question={question ? messageText(question) : undefined}
                            screen={currentContext?.screen}
                          />
                        ) : null}
                      </MessageContent>
                    </Message>
                  );
                })
              )}
              {isAwaitingReply(status, messages) ? <ThinkingIndicator /> : null}
              {error === undefined ? null : (
                <ErrorNotice
                  inline
                  title="That answer didn't finish"
                  message={'The assistant hit a problem or ran out of time mid-answer. Say "try again" — the conversation is intact.'}
                />
              )}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>
        )}

        {view === 'chat' ? (
          // Safe-area padding keeps the composer above the iPhone home bar.
          <div className="shrink-0 border-t border-portal-line p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <PromptInput
              className="relative"
              multiple
              globalDrop
              accept={ATTACHMENT_ACCEPT.join(',')}
              onSubmit={(message) => {
                const text = message.text.trim();
                const files = message.files ?? [];
                if (text.length === 0 && files.length === 0) return;
                void (async () => {
                  // Upload each dropped file to the attachment store first, then
                  // send the message with metadata-only refs — the bytes never
                  // enter the model; a tool reads them only if the request needs it.
                  const auth = await authorizationHeader();
                  const chatId = getCurrentChatId();
                  const refs = (
                    await Promise.all(files.map((file) => uploadChatFile(file, auth, chatId)))
                  ).filter((ref): ref is ChatAttachmentRef => ref !== null);
                  if (files.length > 0 && refs.length === 0) {
                    // Every upload failed (e.g. the API is down) — don't silently
                    // send as if the files went through.
                    window.alert('Could not upload the attached file — please try again.');
                    return;
                  }
                  pendingAttachmentsRef.current = refs;
                  const messageId =
                    typeof crypto !== 'undefined' && 'randomUUID' in crypto
                      ? crypto.randomUUID()
                      : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                  if (refs.length > 0) setSentAttachments((prev) => ({ ...prev, [messageId]: refs }));
                  // The id goes on the message itself: sendMessage's `messageId`
                  // means "replace that existing message" and throws for a new id.
                  const sending = sendMessage({
                    id: messageId,
                    role: 'user',
                    parts: [{ type: 'text', text }],
                    ...(refs.length > 0 ? { metadata: { attachments: refs } } : {}),
                  });
                  // body() has already read the refs for this request; clear so
                  // the next message doesn't resend stale attachments.
                  pendingAttachmentsRef.current = [];
                  await sending;
                })().catch((err: unknown) => {
                  // A send that fails before reaching the server would otherwise
                  // vanish without a trace.
                  console.error('[assistant] send failed', err);
                  window.alert('Could not send your message. Please try again.');
                });
              }}
            >
              <PromptInputBody>
                {/* The textarea owns the whole box; the send button floats over
                    its bottom-right corner instead of a separate footer strip.
                    The input text itself stays 16px — smaller would make iOS
                    zoom the page on focus — only the placeholder shrinks. */}
                <ComposerAttachments />
                <PromptInputTextarea
                  className="pb-10 pl-3.5 placeholder:text-sm"
                  placeholder="Ask the assistant…"
                />
              </PromptInputBody>
              <PromptInputFooter className="pointer-events-none absolute inset-x-0 bottom-0 justify-end border-0 bg-transparent p-2">
                <PromptInputSubmit
                  className="pointer-events-auto"
                  status={status}
                  onStop={() => void stop()}
                />
              </PromptInputFooter>
            </PromptInput>
          </div>
        ) : null}

        <ConfirmDialog
          open={deleting !== null}
          tone="danger"
          title="Delete this chat?"
          message={deleting ? `"${deleting.title}" will be permanently removed.` : ''}
          confirmLabel="Delete chat"
          onConfirm={() => {
            if (deleting) deleteChat.mutate(deleting.id);
          }}
          onCancel={() => {
            if (!deleteChat.isPending) setDeleting(null);
          }}
          pending={deleteChat.isPending}
        />
      </aside>
    </TooltipProvider>
  );
}

/**
 * One saved chat, as a card: question-derived title, when it was last used,
 * and hover actions — pin to top, rename in place, and an ✕ to remove.
 *
 * @param props - The chat and its action handlers.
 * @returns The chat card.
 */
function ChatCard({
  chat,
  onOpen,
  onTogglePin,
  onRename,
  onDelete,
}: {
  readonly chat: ChatListItem;
  readonly onOpen: () => void;
  readonly onTogglePin: () => void;
  readonly onRename: (title: string) => void;
  readonly onDelete: () => void;
}): React.ReactElement {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(chat.title);

  function commitRename(): void {
    const title = draft.trim();
    setRenaming(false);
    if (title.length > 0 && title !== chat.title) onRename(title.slice(0, 120));
    else setDraft(chat.title);
  }

  /** Abandons the rename, restoring the saved title. */
  function cancelRename(): void {
    setDraft(chat.title);
    setRenaming(false);
  }

  const when = chat.updatedAt
    ? new Date(chat.updatedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      }) +
      ' · ' +
      new Date(chat.updatedAt).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="group relative rounded-lg border border-admin-line bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
      {renaming ? (
        <span className="flex items-center gap-1.5">
          <input
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitRename();
              if (event.key === 'Escape') cancelRename();
            }}
            maxLength={120}
            aria-label="Chat name"
            className="min-w-0 flex-1 rounded-md border border-brand/40 px-2 py-1 text-portal-small font-medium focus:outline-none"
          />
          <button
            type="button"
            aria-label="Save name"
            onMouseDown={(event) => event.preventDefault()}
            onClick={commitRename}
            className="grid size-7 shrink-0 cursor-pointer place-items-center rounded-md bg-brand/10 text-brand hover:bg-brand/20"
          >
            <CheckIcon aria-hidden className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Cancel rename"
            onMouseDown={(event) => event.preventDefault()}
            onClick={cancelRename}
            className="grid size-7 shrink-0 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
          >
            <XIcon aria-hidden className="size-4" />
          </button>
        </span>
      ) : (
        <button type="button" onClick={onOpen} className="block w-full cursor-pointer pr-16 text-left">
          <span className="flex items-center gap-1.5 text-portal-small font-bold text-portal-ink">
            {chat.pinned ? (
              <StarIcon aria-hidden className="size-3 shrink-0 fill-amber-400 text-amber-400" />
            ) : null}
            <span className="truncate">{chat.title}</span>
          </span>
          {when ? (
            <span className="mt-0.5 block text-portal-micro text-admin-muted">{when}</span>
          ) : null}
        </button>
      )}

      {renaming ? null : (
        <span className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={chat.pinned ? 'Unpin chat' : 'Pin chat to the top'}
                aria-pressed={chat.pinned}
                onClick={onTogglePin}
                className={cn(
                  'grid size-6 cursor-pointer place-items-center rounded-md hover:bg-admin-subtle',
                  chat.pinned ? 'text-amber-500' : 'text-portal-muted hover:text-amber-500',
                )}
              >
                <StarIcon aria-hidden className={cn('size-3.5', chat.pinned && 'fill-amber-400')} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{chat.pinned ? 'Unpin' : 'Pin to top'}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Rename chat"
                onClick={() => {
                  setDraft(chat.title);
                  setRenaming(true);
                }}
                className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
              >
                <PencilIcon aria-hidden className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Rename</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Remove chat"
                onClick={onDelete}
                className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
              >
                <XIcon aria-hidden className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Remove</TooltipContent>
          </Tooltip>
        </span>
      )}
    </div>
  );
}
